import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api.js';
import { TOKEN_STORAGE_KEY, USER_STORAGE_KEY } from '../config/index.js';

export const AuthContext = createContext(null);

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};

function persistSession(user, token) {
  localStorage.setItem(TOKEN_STORAGE_KEY, token);
  localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
}

function clearSession() {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  localStorage.removeItem(USER_STORAGE_KEY);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount, restore the cached user and re-validate the token against the API.
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const cached = localStorage.getItem(USER_STORAGE_KEY);

    if (!token) {
      setLoading(false);
      return;
    }

    if (cached) {
      try {
        setUser(JSON.parse(cached));
      } catch {
        clearSession();
      }
    }

    api
      .me()
      .then((fresh) => {
        setUser(fresh);
        localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(fresh));
      })
      .catch((err) => {
        // Only a genuine auth failure ends the session. Previously ANY error
        // here logged the user out — a timeout, an offline moment, or the API
        // restarting for a second during a deploy — because the catch could not
        // tell "your token is no longer valid" from "the server did not answer".
        // The cached user stays; the next call re-validates. api.js attaches
        // `status` (and `isTimeout`), so the distinction is available.
        const status = err?.status;
        if (status === 401 || status === 403) {
          clearSession();
          setUser(null);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const { user: u, token } = await api.login({ email, password });
    persistSession(u, token);
    setUser(u);
    return u;
  }, []);

  const register = useCallback(async (email, name, password) => {
    const { user: u, token } = await api.register({ email, name, password });
    persistSession(u, token);
    setUser(u);
    return u;
  }, []);

  const logout = useCallback(() => {
    clearSession();
    setUser(null);
  }, []);

  const value = { user, loading, isAuthenticated: !!user, login, register, logout };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
